import { JsonPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { LoggerService } from '@/core/service/logger/logger.service';
import { ShiptosDropdownComponent } from '@/shared/widgets/shiptos/shiptos-dropdown.component';
import { UserDataStore } from '@/store/user-store/user-data.store';
import { CardInformationComponent } from '@/shared/card-information/card-information.component';
import { DiagnosticSessionService } from '@/core/diagnostics/diagnostic-session.service';

type DebugKind = 'boolean' | 'json' | 'text';

interface DebugDescriptor {
  key: string;
  label: string;
  kind: DebugKind;
}

interface DebugResolvedItem extends DebugDescriptor {
  rendered: string;
  active: boolean | null;
}

interface DebugGroup {
  title: string;
  description: string;
  items: DebugResolvedItem[];
}

const TABLE_GROUP_TITLES = new Set(['Sesion y perfil', 'Flags de negocio']);

const DEBUG_GROUPS: Array<{ title: string; description: string; items: DebugDescriptor[] }> = [
  {
    title: 'Sesion y perfil',
    description: 'Valores base del usuario y del shipto activo.',
    items: [
      { key: 'getUsuario', label: 'getUsuario', kind: 'text' },
      { key: 'getSoldToName', label: 'getSoldToName', kind: 'text' },
      { key: 'getCuit', label: 'getCuit', kind: 'text' },
      { key: 'getNSolicitante', label: 'getNSolicitante', kind: 'text' },
      { key: 'getNDestinatario', label: 'getNDestinatario', kind: 'text' },
      { key: 'getSalesOrg', label: 'getSalesOrg', kind: 'text' },
      { key: 'getUserType', label: 'getUserType', kind: 'text' },
      { key: 'getSubUserRole', label: 'getSubUserRole', kind: 'text' },
      { key: 'isLoaded', label: 'isLoaded', kind: 'boolean' },
      { key: 'isMaster', label: 'isMaster', kind: 'boolean' },
      { key: 'isSubUser', label: 'isSubUser', kind: 'boolean' },
      { key: 'hasValidTermsApproved', label: 'hasValidTermsApproved', kind: 'boolean' },
      { key: 'pendingTyCRetailCount', label: 'pendingTyCRetailCount', kind: 'text' },
    ],
  },
  {
    title: 'Shiptos y contexto',
    description: 'Colecciones y datos derivados del contexto actual.',
    items: [
      { key: 'getShiptoSelected', label: 'getShiptoSelected', kind: 'json' },
      { key: 'getSelectShiptos', label: 'getSelectShiptos', kind: 'json' },
      { key: 'getChannelsDistribution', label: 'getChannelsDistribution', kind: 'json' },
      { key: 'getSectors', label: 'getSectors', kind: 'json' },
      { key: 'getChannelAndSectors', label: 'getChannelAndSectors', kind: 'json' },
      { key: 'getCreditControlArea', label: 'getCreditControlArea', kind: 'json' },
      { key: 'getCreditControlAreaShipto', label: 'getCreditControlAreaShipto', kind: 'json' },
      { key: 'getDatosDeFacturacion', label: 'getDatosDeFacturacion', kind: 'json' },
      { key: 'getShiptos', label: 'getShiptos', kind: 'json' },
      { key: 'getSections', label: 'getSections', kind: 'json' },
      { key: 'getSectionsObject', label: 'getSectionsObject', kind: 'json' },
      { key: 'getPBL', label: 'getPBL', kind: 'json' },
      { key: 'getPblAsrList', label: 'getPblAsrList', kind: 'json' },
      { key: 'isPblAsrResolved', label: 'isPblAsrResolved', kind: 'boolean' },
      { key: 'hasAnyShiptoWithPbl', label: 'hasAnyShiptoWithPbl', kind: 'boolean' },
      { key: 'getAllShiptosWithIds', label: 'getAllShiptosWithIds', kind: 'json' },
      { key: 'getChannelsDistributionList', label: 'getChannelsDistributionList', kind: 'json' },
      { key: 'getSectorsList', label: 'getSectorsList', kind: 'json' },
      { key: 'getPlantasByShiptos', label: 'getPlantasByShiptos', kind: 'json' },
      { key: 'getSupplyCentersList', label: 'getSupplyCentersList', kind: 'json' },
      { key: 'getExpeditionList', label: 'getExpeditionList', kind: 'json' },
      { key: 'getAllContacts', label: 'getAllContacts', kind: 'json' },
      { key: 'getContactsByDestinatario', label: 'getContactsByDestinatario', kind: 'json' },
      { key: 'getShiptoPermissions', label: 'getShiptoPermissions', kind: 'json' },
    ],
  },
  {
    title: 'Flags de negocio',
    description: 'Todas las validaciones y banderas calculadas del store.',
    items: [
      { key: 'isCif', label: 'isCif', kind: 'boolean' },
      { key: 'isFob', label: 'isFob', kind: 'boolean' },
      { key: 'isToma', label: 'isToma', kind: 'boolean' },
      { key: 'isRetail', label: 'isRetail', kind: 'boolean' },
      { key: 'isFuels', label: 'isFuels', kind: 'boolean' },
      { key: 'isTransporte', label: 'isTransporte', kind: 'boolean' },
      { key: 'isB2B', label: 'isB2B', kind: 'boolean' },
      { key: 'isLubes', label: 'isLubes', kind: 'boolean' },
      { key: 'isMarine', label: 'isMarine', kind: 'boolean' },
      { key: 'isAgro', label: 'isAgro', kind: 'boolean' },
      { key: 'isAxionDirecto', label: 'isAxionDirecto', kind: 'boolean' },
      { key: 'isAxion', label: 'isAxion', kind: 'boolean' },
      { key: 'isAxionCardView', label: 'isAxionCardView', kind: 'boolean' },
      { key: 'isAxionCardDual', label: 'isAxionCardDual', kind: 'boolean' },
      { key: 'isAxionCardPuro', label: 'isAxionCardPuro', kind: 'boolean' },
      { key: 'isAxionCardRetail', label: 'isAxionCardRetail', kind: 'boolean' },
      { key: 'isQuimicos', label: 'isQuimicos', kind: 'boolean' },
      { key: 'isB2BFuels', label: 'isB2BFuels', kind: 'boolean' },
      { key: 'isB2BLubes', label: 'isB2BLubes', kind: 'boolean' },
      { key: 'isRetailDocFinancieros', label: 'isRetailDocFinancieros', kind: 'boolean' },
      { key: 'isRetailFuels', label: 'isRetailFuels', kind: 'boolean' },
      { key: 'isRetailLubes', label: 'isRetailLubes', kind: 'boolean' },
      { key: 'isRetailContable', label: 'isRetailContable', kind: 'boolean' },
      { key: 'isAgroLubes', label: 'isAgroLubes', kind: 'boolean' },
      { key: 'isAgroFuels', label: 'isAgroFuels', kind: 'boolean' },
      { key: 'isMarineLubes', label: 'isMarineLubes', kind: 'boolean' },
      { key: 'isMarineInternacional', label: 'isMarineInternacional', kind: 'boolean' },
      { key: 'IsTurnosDeCargaLubes', label: 'IsTurnosDeCargaLubes', kind: 'boolean' },
      { key: 'isDetalleEntregaLubes', label: 'isDetalleEntregaLubes', kind: 'boolean' },
      { key: 'isCompass', label: 'isCompass', kind: 'boolean' },
      { key: 'isLubesDistributor', label: 'isLubesDistributor', kind: 'boolean' },
      { key: 'isRCOR', label: 'isRCOR', kind: 'boolean' },
      { key: 'isRetailFuelsRCOR', label: 'isRetailFuelsRCOR', kind: 'boolean' },
      { key: 'isSpot', label: 'isSpot', kind: 'boolean' },
      { key: 'isRetailSpot', label: 'isRetailSpot', kind: 'boolean' },
      { key: 'isMarketplace', label: 'isMarketplace', kind: 'boolean' },
      { key: 'isRetailMarketplace', label: 'isRetailMarketplace', kind: 'boolean' },
      { key: 'isAgroMarketplace', label: 'isAgroMarketplace', kind: 'boolean' },
      { key: 'isFusion', label: 'isFusion', kind: 'boolean' },
      { key: 'isPesados', label: 'isPesados', kind: 'boolean' },
      { key: 'isTag', label: 'isTag', kind: 'boolean' },
      { key: 'isDJStore', label: 'isDJStore', kind: 'boolean' },
      { key: 'isSandwichStand', label: 'isSandwichStand', kind: 'boolean' },
      { key: 'isShiptoSelectedWithPbl', label: 'isShiptoSelectedWithPbl', kind: 'boolean' },
      { key: 'isAsrUser', label: 'isAsrUser', kind: 'boolean' },
    ],
  },
];

@Component({
  selector: 'app-session-page',
  standalone: true,
  imports: [JsonPipe, ShiptosDropdownComponent, CardInformationComponent],
  templateUrl: './session-page.account-like.html',
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SessionPageComponent {
  userDataStore = inject(UserDataStore);
  loggerService = inject(LoggerService);
  diagnosticSession = inject(DiagnosticSessionService);
  loggerEnabled = signal(this.loggerService.isPersistedEnabled());
  propertySearch = signal<Record<string, string>>({
    'Sesion y perfil': '',
    'Flags de negocio': '',
  });

  toggleLogger(): void {
    const nextValue = !this.loggerEnabled();
    this.loggerService.setEnabled(nextValue);
    this.loggerEnabled.set(nextValue);
  }

  toggleLogicLogger(): void {
    this.diagnosticSession.toggleFromControlPanel();
  }

  isTableGroup(title: string): boolean {
    return TABLE_GROUP_TITLES.has(title);
  }

  getPropertySearch(title: string): string {
    return this.propertySearch()[title] ?? '';
  }

  setPropertySearch(title: string, event: Event): void {
    const target = event.target as HTMLInputElement | null;
    const value = target?.value ?? '';

    this.propertySearch.update(search => ({ ...search, [title]: value }));
  }

  getFilteredGroupItems(group: DebugGroup): DebugResolvedItem[] {
    const search = this.getPropertySearch(group.title).trim().toLowerCase();

    if (!search) {
      return group.items;
    }

    return group.items.filter(
      item => item.key.toLowerCase().includes(search) || item.label.toLowerCase().includes(search),
    );
  }

  readonly debugGroups = computed<DebugGroup[]>(() => {
    const currentShipto = this.userDataStore.getNDestinatario();
    const contactsByShipto = currentShipto ? this.userDataStore.getContactsByDestinatario()(currentShipto) : [];

    return DEBUG_GROUPS.map(group => ({
      title: group.title,
      description: group.description,
      items: group.items.map(item => {
        const resolvedValue = this.resolveState(item.key, contactsByShipto);
        return {
          ...item,
          rendered: this.renderValue(resolvedValue, item.kind),
          active: typeof resolvedValue === 'boolean' ? resolvedValue : null,
        };
      }),
    }));
  });

  private resolveState(key: string, contactsByShipto: unknown[]): unknown {
    if (key === 'getContactsByDestinatario') {
      return contactsByShipto;
    }

    const value = (this.userDataStore as Record<string, unknown>)[key];
    if (typeof value === 'function') {
      try {
        return (value as () => unknown)();
      } catch {
        return '[unavailable]';
      }
    }

    return value;
  }

  private renderValue(value: unknown, kind: DebugKind): string {
    if (kind === 'boolean') {
      return value === true ? 'true' : 'false';
    }

    if (value === null || value === undefined || value === '') {
      return 'No disponible';
    }

    if (kind === 'text') {
      return String(value);
    }

    try {
      return typeof value === 'string' ? value : JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }
}
